import { useEffect, useState, useMemo } from 'react';
import pickBy from 'lodash/pickBy';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import pushRoute from '@utils/pushRoute';
import isEmpty from '@utils/isEmpty';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { useSnackbar } from '@stores/snackbar';
import {
  PRACTICE_STARTED,
  PRACTICE_EXAM_PAPERS_CLICKED,
  SYLLABUS_PRACTICE_CLICKED,
  OPEN_IN_APP_CLICKED
} from '@constants/segment';
import { createSession } from '@actions/practiceSession';
import usePracticeSegmentAnalytics from '@cont/Practice/usePracticeSegmentAnalytics';
import { practiceSessionTypes } from '@actions-constants/index';
import { topologyLevels } from '@constants/index';
import { getTopologyName } from '@models/Topology';
import {
  getPracticeAvailableLanguages,
  getUserPreferenceLanguage
} from '@selectors/practiceSession';
import { getIsSubscribedToGoal } from '@selectors/subscription';
import { segment } from '@actions/analytics';
import usePracticeStore from '@stores/practice';
import usePrimeTrialPromoModalStore from '@stores/prime-pitch/store';
import { PrimeTrialPromoContextualId } from '@stores/prime-pitch/types';
import usePrimeQuotaStore from '@stores/prime-quota/store';
import { PrimeMode } from '@stores/prime-quota/types';
import { readCookie } from '@utils/cookies';
import { useIsMobile } from '@stores/AppCommonData';
import useOneLinkSmartScriptV2 from '@hooks/useOneLinkSmartScriptV2';
import { openToggleableElement } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import useConceptFetch from './useConceptFetch';
import {
  PRACTICE_TYPE,
  YEAR_FILTER_TYPE,
  PRACTICE_FLOW_TYPE
} from '../../constants';
import { conceptHasQuestions, getCardType } from '../../utils';

const EMPTY_ARRAY = [];

const useConceptSelector = (props = {}) => {
  const { topicGroupUID, flowType, lastPrimarySource = '' } = props;
  const {
    setPracticeSelectedTopic,
    practiceSelectedTopic: selectedTopic = {}
  } = usePracticeStore();
  const dispatch = useDispatch();
  const router = useRouter();
  const segmentAnalytics = usePracticeSegmentAnalytics();
  const [currentFilter, setCurrentFilter] = useState({});
  const [selectedConcepts, setSelectedConcepts] = useState({});
  useConceptFetch(currentFilter);

  const { goalSlug, goalUID, conceptUid } = router.query;
  const isLoggedIn = useSelector((state) => state.auth?.isLoggedIn);
  const isSubscribed = useSelector(getIsSubscribedToGoal(goalUID));
  const isMobile = useIsMobile();

  const openPrimeTrialPromoModal = usePrimeTrialPromoModalStore(
    (state) => state.openModal
  );
  const consumeQuota = usePrimeQuotaStore((state) => state.consumeQuota);
  const getQuotaForMode = usePrimeQuotaStore((state) => state.getQuotaForMode);

  const generatedAppLink = useOneLinkSmartScriptV2({
    campaign: 'practice page'
  });

  const filters = useSelectorWithShallowEqual(
    ({ practiceSession }) =>
      practiceSession.filters[selectedTopic.uid] || EMPTY_ARRAY
  );

  const availableLanguageData = useSelectorWithShallowEqual(
    getPracticeAvailableLanguages(router.query.goalUID)
  );

  const language = useSelectorWithShallowEqual(
    getUserPreferenceLanguage(router.query.goalUID)
  );

  const selectedQuestionCount = useSelectorWithShallowEqual(
    ({ practiceSession }) =>
      isEmpty(practiceSession.selectedQuestionCount)
        ? 10
        : practiceSession.selectedQuestionCount
  );

  const practiceTopics = useSelectorWithShallowEqual(
    ({ practiceSession }) =>
      practiceSession.topologies[selectedTopic.uid] || EMPTY_ARRAY
  );

  const { open: showSnackbar } = useSnackbar();

  const exams = useMemo(
    () =>
      filters.map(({ name, uid, year: years }) => {
        const yearsData = [
          {
            name: 'All Years',
            uid: YEAR_FILTER_TYPE.ALL,
            raw: []
          }
        ];

        years?.forEach((year) => {
          const nameYear = year[1]
            ? `Year ${year[0]} to ${year[1]}`
            : `Year ${year[0]}`;

          const startYear = year?.[0];
          const endYear = year?.[1] || year?.[0];

          yearsData.push({
            name: nameYear,
            uid: `${startYear}_${endYear}`,
            raw: year
          });
        });

        return {
          name,
          uid,
          years: yearsData
        };
      }),
    [filters]
  );

  useEffect(() => {
    if (!exams.length || selectedTopic?.type !== PRACTICE_TYPE.PYQ) {
      return;
    }

    const startYear = exams[0].years?.[0]?.[0] || null;
    const endYear =
      exams[0].years?.[0]?.[1] || exams[0].years?.[0]?.[0] || null;

    setCurrentFilter({
      examUID: exams[0].uid,
      yearUID: YEAR_FILTER_TYPE.ALL,
      startYear,
      endYear
    });
  }, [exams, selectedTopic?.type]);

  const [allConceptSelected, someConceptSelected] = useMemo(() => {
    const totalConcepts = practiceTopics?.length || 0;
    let isAllSelected = true;
    let isSomeSelected = false;

    for (let i = 0; i < totalConcepts; i += 1) {
      if (selectedConcepts[practiceTopics[i].uid]) {
        isSomeSelected = true;
      } else {
        isAllSelected = false;
      }
    }

    return [isAllSelected, isSomeSelected];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConcepts]);

  const practiceTopicsToAvoid = useMemo(() => {
    const data = {};

    practiceTopics?.forEach((concept) => {
      if (!conceptHasQuestions(concept?.is_threshold_exceeded)) {
        data[concept.uid] = true;
      }
    });

    return data;
  }, [practiceTopics]);

  const isNotFullPracticeType = selectedTopic.type !== PRACTICE_TYPE.FULL;
  const isEachPracticeType = selectedTopic.type === PRACTICE_TYPE.EACH_TOPIC;
  const isSyllabus = flowType === PRACTICE_FLOW_TYPE.SYLLABUS_PRACTICE;
  const selectedExam = exams?.find(({ uid }) => uid === currentFilter.examUID);
  const selectedYear = selectedExam?.years?.find(
    ({ uid }) => uid === currentFilter.yearUID
  );

  const selectAllConcepts = () => {
    const data = {};

    practiceTopics?.forEach((concept) => {
      data[concept.uid] = true;
    });

    setSelectedConcepts(data);
  };

  useEffect(() => {
    selectAllConcepts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceTopics]);

  useEffect(() => {
    if (selectedExam && selectedYear && currentFilter) {
      segmentAnalytics(PRACTICE_EXAM_PAPERS_CLICKED, {
        exam_name: selectedExam.name,
        exam_timeline: selectedYear.name,
        topology_id: selectedTopic.uid,
        topology_name: selectedTopic.name,
        topology_level: getTopologyName[selectedTopic.level]
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilter]);

  const handleConceptClick = (e) => {
    if (e.target.value === 'allSelected') {
      if (e.target.checked) {
        selectAllConcepts();
      } else {
        setSelectedConcepts({});
      }
    } else {
      setSelectedConcepts((concepts) => {
        const filteredConcepts = isEachPracticeType
          ? pickBy(concepts, (_, key) => {
              return !practiceTopicsToAvoid[key];
            })
          : concepts;

        const { [e.target.value]: selectedConcept, ...rest } = filteredConcepts;

        if (e.target.checked) {
          return {
            ...filteredConcepts,
            [e.target.value]: e.target.checked
          };
        }

        return {
          ...rest
        };
      });
    }
  };

  const handleExamChange = ({ uid, years }) => {
    setCurrentFilter({
      examUID: uid,
      yearUID: years?.[0]?.uid,
      startYear: years?.[0]?.raw?.[0],
      endYear: years?.[0]?.raw?.[1] || years?.[0]?.raw?.[0]
    });
  };

  const handleYearChange = ({ raw, uid }) => {
    setCurrentFilter({
      examUID: selectedExam?.uid,
      yearUID: uid,
      startYear: raw[0],
      endYear: raw[1] || raw[0]
    });
  };

  const handleStartPractice = async () => {
    // Check if user is logged in
    if (!isLoggedIn) {
      openToggleableElement(LOGIN_DRAWER);
      return;
    }

    if (!selectedQuestionCount) {
      showSnackbar({
        message: 'Unable to start the practice. Please try again',
        type: 'error'
      });

      return;
    }

    // Mobile users: Open in app
    if (isMobile) {
      segmentAnalytics(OPEN_IN_APP_CLICKED, {
        page: 'practice',
        topology_id: selectedTopic.uid,
        topology_name: selectedTopic.name
      });
      if (generatedAppLink) {
        window.open(generatedAppLink, '_blank');
      }
      return;
    }

    // Check and consume quota for free users before starting practice
    if (!isSubscribed) {
      const currentQuota = getQuotaForMode(PrimeMode.PRACTICE);

      // Check if quota is exhausted before consuming
      if (currentQuota.isQuotaExhausted) {
        openPrimeTrialPromoModal(
          PrimeTrialPromoContextualId.QUESTION_QUOTA,
          goalUID,
          goalSlug,
          {
            dismissNavigateTo: null
          }
        );
        return;
      }

      // Consume quota if available
      const accessToken = readCookie('accessToken');
      await consumeQuota({
        goalUID,
        mode: PrimeMode.PRACTICE,
        accessToken
      });
    }

    const topologyUIDsCommaSeperated = Object.keys(selectedConcepts).join(',');
    const topologyUIDs = isNotFullPracticeType
      ? topologyUIDsCommaSeperated
      : selectedTopic.uid;
    const practiceStartPayload = {
      no_of_questions: selectedQuestionCount,
      uid: topologyUIDs,
      language: availableLanguageData?.user_preference ?? 0,
      ...(selectedTopic?.type === PRACTICE_TYPE.PYQ && {
        pyq_practice: true,
        exam_uid: selectedExam?.uid,
        exam_year: selectedYear?.raw
      }),
      ...(selectedTopic?.type === PRACTICE_TYPE.INCORRECT && {
        incorrect_attempts_practice: true
      })
    };

    dispatch(createSession(practiceStartPayload)).then((response) => {
      if (
        response.type ===
        `${practiceSessionTypes.CREATE_PRACTICE_SESSION}_SUCCESS`
      ) {
        let query = {
          goalSlug,
          goalUID,
          topicUID: selectedTopic.uid,
          conceptUid,
          topic_type: selectedTopic.type,
          topic_group: selectedTopic.parentUID
        };
        if (topicGroupUID && flowType) {
          query = {
            ...query,
            type: flowType,
            topic_group_parent: topicGroupUID
          };
        }
        if (Object.keys(selectedConcepts).length === 1 && isSyllabus) {
          query = {
            ...query,
            subconceptUID: Object.keys(selectedConcepts).join(',')
          };
        }

        const topologyNames = practiceTopics.reduce((acc, { uid, name }) => {
          if (selectedConcepts[uid]) {
            return acc ? `${acc}, ${name}` : `${name}`;
          }

          return acc;
        }, '');

        const eventData = {
          topic_uid: selectedTopic.parentUID,
          practice_questions: selectedQuestionCount,
          is_resumed: false,
          topology_id: topologyUIDsCommaSeperated,
          topology_name: topologyNames,
          topology_level: getTopologyName[topologyLevels.TOPIC],
          last_primary_source: lastPrimarySource,
          last_primary_source_section: getCardType(selectedTopic.type),
          language: language?.display_name ?? 'NA',
          practice_type: 'Practice',
          free_trail_present: !isSubscribed
        };

        if (flowType === PRACTICE_FLOW_TYPE.SYLLABUS_PRACTICE) {
          segment.track(SYLLABUS_PRACTICE_CLICKED, {
            ...eventData,
            last_primary_source_section: 'Syllabus Practice click'
          });
        }

        pushRoute({
          pathname: '/goal/[goalSlug]/[goalUID]/practice/[topicUID]',
          query
        }).then(() => {
          segmentAnalytics(PRACTICE_STARTED, eventData);

          setPracticeSelectedTopic({});
        });
      } else {
        showSnackbar({
          message: 'Unable to start the practice. Please try again',
          type: 'error'
        });
      }
    });
  };

  return {
    exams,
    allConceptSelected,
    someConceptSelected,
    selectedExam,
    selectedYear,
    selectedConcepts,
    handleExamChange,
    handleYearChange,
    handleConceptClick,
    handleStartPractice
  };
};

export default useConceptSelector;
